import React, { Component } from 'react';

import { intersection, path } from 'ramda';

import { Field, reduxForm } from 'redux-form';

import Button from '@material-ui/core/Button';
import CardContent from '@material-ui/core/CardContent';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';

import AutoCompleteWithChipsField from '../fields/AutoCompleteWithChipsField';
import CheckboxField from '../fields/CheckboxField';
import DecIncTextField from '../fields/DecIncTextField';
import DurationTextField from '../fields/DurationTextField';
import SelectField from '../fields/SelectField';
import TimePickerField from '../fields/TimePickerField';


class RouteBlockageForm extends Component {
  componentDidMount () {
    const {
      companies,
      initialValues,
      railways,
      readEndpoint,
      readEndpointDedup,
    } = this.props;

    if (!companies || companies.length === 0) {
      readEndpointDedup('company?filter[deleted-at__isnull]=True');
    }

    if (!railways || railways.length === 0) {
      readEndpointDedup('railway');
    }

    // Fetch relationships for selected railway of an existing RouteBlockage
    if  (initialValues && initialValues.railway) {
      readEndpoint(`railway/${initialValues.railway}`);
    }
  }

  componentDidUpdate(prevProps) {
    const { autofill, initialValues, railwayId, readEndpoint } = this.props;

    const hasInitialRailway = !!(initialValues && initialValues.railway);
    const hadRailway = !!(prevProps.railwayId);
    const hasRailwayChanged = railwayId !== prevProps.railwayId;

    // Fetch the railway if selected
    if (hasRailwayChanged && ((hasInitialRailway && hadRailway) || !hasInitialRailway)) {
      readEndpoint(`railway/${railwayId}`).then(res => {
        /**
         * Once the detail route of the selected railway is loaded,
         * set the `companies` field to the default servicing
         * companies as specified.
         */
        const servicingCompanies =
          path(['body', 'data', 'relationships', 'servicing-companies', 'data'], res) || [];

        const servicingCompanyIds = servicingCompanies.map(c => c.id);
        autofill('companies', servicingCompanyIds);
      });
    }
  }

  render() {
    const {
      error,
      change,
      handleSubmit,
      onEndBlockage,
      pristine,
      submitting,
      railways,
      companies,
      stops,
      initialValues,
      companiesAreInvited,
      isAdmin,
    } = this.props;

    const isEditing = !!initialValues;

    return (
      <form onSubmit={handleSubmit}>
        <CardContent>
          {error && <Typography variant="subtitle1">{error}</Typography>}

          <Field
            name="expected_duration"
            component={DurationTextField}
            floatingLabelText="Duur"
          />

          <Field
            name="start_at"
            component={TimePickerField}
            label="Starttijd"
          />

          <Field
            name="railway"
            id="railway"
            component={SelectField}
            label="Traject"
            onChange={(e, newValue, previousValue) => {
              if (newValue !== previousValue) {
                change('stop_from', null);
                change('stop_to', null);
              }
            }}
          >
            {railways.map(railway => (
              <MenuItem key={railway.id} value={railway.id}>{railway.attributes.name}</MenuItem>
            ))}
          </Field>

          <Field
            name="stop_from"
            id="stop_from"
            component={SelectField}
            label="Van station"
            disabled={stops.length === 0}
          >
            {stops.map((stop) => {
              if (!stop.attributes.name.match(/^Zone/g))
                return (
                  <MenuItem key={stop.id} value={stop.id}>{stop.attributes.name}</MenuItem>
                )
            })}
          </Field>

          <Field
            name="stop_to"
            id="stop_to"
            component={SelectField}
            label="Tot station"
            disabled={stops.length === 0}
          >
            {stops.map((stop) => {
              if (!stop.attributes.name.match(/^Zone/g))
                return (
                  <MenuItem key={stop.id} value={stop.id}>{stop.attributes.name}</MenuItem>
                )
            })}
          </Field>

          <Field
            name="vehicles_required"
            component={DecIncTextField}
            label="Aantal bussen aanvragen"
          />

          <Field
            name="preferred_companies"
            component={AutoCompleteWithChipsField}
            label="Geprefereerde bedrijven"
            suggestions={companies.map(company => ({ label: company.attributes.name, value: company.id }))}
          />

          <Field
            id="invite_non_preferred_companies"
            name="invite_non_preferred_companies"
            component={CheckboxField}
            label="Nodig overige bedrijven uit."
            color="primary"
            style={{
              marginTop: 28,
              marginBottom: 16,
            }}
            disabled={initialValues && initialValues.invite_non_preferred_companies}
          />

          <Field
            name="companies"
            component={AutoCompleteWithChipsField}
            label="Overige bedrijven"
            suggestions={companies.map(company => ({ label: company.attributes.name, value: company.id }))}
            shadeField={!companiesAreInvited}
          />
        </CardContent>

        {isAdmin && (
          <Button
            fullWidth
            disabled={pristine || submitting}
            variant="contained"
            color="primary"
            type="submit"
          >
            {isEditing ? "Stremming wijzigen" : "Stremming starten"}
          </Button>
        )}
        {isAdmin && isEditing && (
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={() => onEndBlockage()}
          >
            Stremming beëindigen
          </Button>
        )}
      </form>
    );
  }
}

// Decorate the form component
RouteBlockageForm = reduxForm({
  form: 'routeBlockageForm', // a unique name for this form
  validate: values => {
    const errors = {};

    if (!values.expected_duration) {
      errors.expected_duration = 'Verplicht'
    }

    if (!values.railway) {
      errors.railway = 'Verplicht'
    }

    if (!values.stop_from) {
      errors.stop_from = 'Verplicht'
    }

    if (!values.stop_to) {
      errors.stop_to = 'Verplicht'
    }

    if (!values.vehicles_required) {
      errors.vehicles_required = 'Verplicht'
    }

    /**
     * `companies` is not required, but submit handlers expect it to be an array.
     */
    if (!values.companies) {
      values.companies = [];
    }

    /**
     * `preferred_companies` is not required, but submit handlers expect it to be
     * an array.
     */
    if (!values.preferred_companies) {
      values.preferred_companies = [];
    }

    const companiesIntersection = intersection(values.preferred_companies, values.companies);

    if (companiesIntersection.length > 0) {
      errors.preferred_companies = ['Een bedrijf mag niet in beide lijsten met bedrijven voorkomen', companiesIntersection];
      errors.companies = ['Een bedrijf mag niet in beide lijsten met bedrijven voorkomen', companiesIntersection];
    }

    return errors;
  },
})(RouteBlockageForm);

export default RouteBlockageForm;
